import { AuditTrailEntryDto } from '@common/audit-trail/auditTrailTypes';
import { t } from 'utils/getTranslations';
import {
    ActionDot,
    ActionPill,
    AuthorAvatar,
    AuthorName,
    EntityTypeBadge,
    RowIdButton,
    TransactionButton,
} from './AuditTrailBadges';
import { AuditTrailFieldRow } from './AuditTrailFieldRow';
import { dateBucketLabel, formatAuditName, parseChangedAt, timeLabel } from '@common/audit-trail/auditTrailFormat';
import { auditTrailDayLabels } from './auditTrailLabels';
import { ValuePreview } from './ValuePreviewDialog';

function fieldChangesLabel(count: number): string {
    return count === 1 ? t('audit-trail.one-field-change') : t('audit-trail.field-changes', { count: String(count) });
}

export function AuditTrailEntryCard({
    entry,
    onTransactionClick,
    onRowIdClick,
    onAuthorClick,
    onPreview,
}: {
    entry: AuditTrailEntryDto;
    onTransactionClick: (transactionId: string) => void;
    onRowIdClick: (rowId: string) => void;
    onAuthorClick: (authorId: number) => void;
    onPreview: (preview: ValuePreview) => void;
}) {
    const entityTypeLabel = formatAuditName(entry.tableName);
    const changedAt = parseChangedAt(entry.changedAt);

    return (
        <div className="bg-paper shadow-card overflow-hidden rounded-xl">
            <div className="border-grey-200 flex items-center gap-3 border-b px-4.5 py-3.25">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <ActionDot operationType={entry.operationType} />
                        <EntityTypeBadge label={entityTypeLabel} />
                        <RowIdButton
                            rowId={entry.rowId}
                            label={entry.rowLabel}
                            className="text-[12.5px]"
                            onClick={onRowIdClick}
                        />
                        <ActionPill operationType={entry.operationType} />
                    </div>
                    <div className="new:text-muted-foreground mt-[3px] truncate text-xs text-[#9A9A9A]">
                        {fieldChangesLabel(entry.changedProperties.length)}
                    </div>
                </div>
                <div className="flex flex-none items-center gap-3">
                    <div className="text-right">
                        <div className="text-grey-600 text-[12.5px] font-medium whitespace-nowrap">
                            <AuthorName entry={entry} onClick={onAuthorClick} />
                        </div>
                        <div className="new:text-muted-foreground new:text-caption mt-px text-[11px] whitespace-nowrap text-[#B0B0B0]">
                            {dateBucketLabel(changedAt, auditTrailDayLabels())} · {timeLabel(changedAt)}
                        </div>
                    </div>
                    <AuthorAvatar entry={entry} size="sm" />
                    {entry.transactionId && (
                        <TransactionButton transactionId={entry.transactionId} onClick={onTransactionClick} />
                    )}
                </div>
            </div>
            <div className="px-4.5 pt-0.5 pb-3">
                {entry.changedProperties.length === 0 && (
                    <div className="text-grey-400 new:text-muted-foreground py-[9px] text-[13px]">
                        {t('audit-trail.no-changed-properties')}
                    </div>
                )}
                {entry.changedProperties.map((property) => (
                    <AuditTrailFieldRow
                        key={property}
                        property={property}
                        oldValue={entry.oldValues[property]}
                        newValue={entry.newValues[property]}
                        oldLabel={entry.oldValueLabels?.[property]}
                        newLabel={entry.newValueLabels?.[property]}
                        entityTypeLabel={entityTypeLabel}
                        onPreview={onPreview}
                    />
                ))}
            </div>
        </div>
    );
}
